import { isString } from 'fitify-types/src/type-guards/common'
import { SfExclamationMarkTriangleIcon } from 'fitify-ui/src/Icon/fill/SfExclamationMarkTriangleIcon'
import { Header } from 'fitify-ui-onboarding/src/components/GenderFunnel/Header/Header'
import type { HeaderProps } from 'fitify-ui-onboarding/src/components/GenderFunnel/Header/Header.Types'
import { InfoCard } from 'fitify-ui-onboarding/src/components/GenderFunnel/InfoCard/InfoCard'
import dynamic from 'next/dynamic'
import { type PropsWithChildren, type ReactNode, useMemo } from 'react'
import type { FieldErrors, FieldValues, FormState } from 'react-hook-form'

import { CookieBotStyles } from 'components/CookieBotStyles/CookieBotStyles'
import { BottomContent } from 'components/GenderFunnel/Layout/BottomContent/BottomContent'
import {
  type LayoutContentVariant,
  LayoutContent,
} from 'components/GenderFunnel/Layout/Layout.Styled'
import {
  LayoutContainer,
  type LayoutContainerProps,
} from 'components/GenderFunnel/Layout/LayoutContainer/LayoutContainer'
import {
  useLayout,
  type UseLayoutNavigationProps,
} from 'components/GenderFunnel/Layout/useLayout'
import { useLayoutTheme } from 'components/GenderFunnel/Layout/useLayoutTheme'
import { PageTitle } from 'components/PageTitle'
import { ThemeProvider } from 'components/ThemeProvider'

const DevStepSelector =
  process.env.NEXT_PUBLIC_APP_ENV === 'dev'
    ? dynamic(
        () =>
          import('components/dev/DevStepSelector/DevStepSelector').then(
            (module) => module.DevStepSelector
          ),
        { ssr: false }
      )
    : null

type Props = PropsWithChildren<
  {
    backgroundImage?: ReactNode
    bottomContent?: ReactNode
    form?: LayoutContainerProps['form'] & {
      state: FormState<FieldValues>
    }
    title: string
    user?: HeaderProps['user']
    variant?: LayoutContentVariant
  } & UseLayoutNavigationProps
>

export const Layout = ({
  backgroundImage,
  bottomContent,
  children,
  form,
  hasNativeBackAction,
  hasRouterBackButton,
  title,
  user,
  variant,
}: Props) => {
  const { handlePreviousClick, headerStepper } = useLayout({
    hasNativeBackAction,
    hasRouterBackButton,
  })
  const theme = useLayoutTheme()

  const errorMessages = useMemo<string[]>(() => {
    const getErrorMessages = (errors?: FieldErrors): string[] => {
      try {
        if (errors) {
          return Object.values(errors).reduce<string[]>((acc, error) => {
            if (isString(error?.message)) {
              acc.push(error.message)
            } else if (typeof error === 'object') {
              acc.push(...getErrorMessages(error as FieldErrors))
            }
            return acc
          }, [])
        }
        return []
      } catch {
        return []
      }
    }

    return getErrorMessages(form?.state.errors)
    // Use form state as dependency to make sure errors are updated
  }, [form?.state])

  const hasBottomContent = Boolean(bottomContent) || errorMessages.length > 0

  return (
    <ThemeProvider theme={theme}>
      <CookieBotStyles />
      <PageTitle title={title} noIndex />
      <Header
        devSlot={DevStepSelector ? <DevStepSelector /> : undefined}
        handlePreviousClick={handlePreviousClick}
        stepper={headerStepper}
        user={user}
      />
      <LayoutContainer form={form}>
        <LayoutContent $hasBottomContent={hasBottomContent} $variant={variant}>
          {children}
        </LayoutContent>
        {hasBottomContent && (
          <BottomContent>
            {errorMessages.map((errorMessage, index) => (
              <InfoCard
                key={`${index}-${errorMessage}`}
                icon={<SfExclamationMarkTriangleIcon />}
                variant="red"
                body={errorMessage}
              />
            ))}
            {bottomContent}
          </BottomContent>
        )}
        {backgroundImage}
      </LayoutContainer>
    </ThemeProvider>
  )
}
